<template>
  <div class="relative">
    <div v-show="isLeftBtnVisible" class="absolute -left-6 top-0 hidden w-4 cursor-pointer items-center justify-center xl:flex" :class="`${props.scrollButtonBoxStyle}`" @click="scrollLeft">
      <img alt="" class="h-6 w-3" src="~@/assets/img/parts/top-left.svg" />
    </div>
    <div ref="scrollBox" class="slide-scrollbar flex overflow-x-scroll">
      <div :class="['flex gap-2', props.isSingleChildFilled ? 'min-w-full' : '']">
        <slot></slot>
      </div>
    </div>
    <div v-show="isRightBtnVisible" class="absolute -right-6 top-0 hidden w-4 cursor-pointer items-center justify-center xl:flex" :class="`${props.scrollButtonBoxStyle}`" @click="scrollRight">
      <img alt="" class="h-6 w-3" src="~@/assets/img/parts/top-right.svg" />
    </div>
  </div>
</template>

<script lang="ts" setup>
interface Props {
  scrollButtonBoxStyle?: string;
  refresh?: boolean;
  /**
   * スライドが1枚のとき、親要素幅を埋めるか？
   */
  isSingleChildFilled?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  scrollButtonBoxStyle: '',
  refresh: false,
  isSingleChildFilled: false,
});

const scrollBox = ref<HTMLElement>();
const isLeftBtnVisible = ref<boolean>(false);
const isRightBtnVisible = ref<boolean>(false);
let mutationObserver: MutationObserver | null = null;

const scroll = (position: number) => {
  if (scrollBox.value == null) return;

  scrollBox.value.scrollTo({
    left: position,
    behavior: 'smooth',
  });
};

const setBtnVisibility = () => {
  if (scrollBox.value == null) return;
  const scrollWidth = scrollBox.value.scrollWidth;
  const clientWidth = scrollBox.value.clientWidth;
  const scrollLeft = scrollBox.value.scrollLeft;

  // スクロール幅がクライアント幅よりも小さい場合はボタンを表示しない
  if (scrollWidth <= clientWidth) {
    isLeftBtnVisible.value = false;
    isRightBtnVisible.value = false;
    return;
  }

  // 左端にいる場合は右側だけボタン表示
  if (scrollLeft === 0) {
    isLeftBtnVisible.value = false;
    isRightBtnVisible.value = true;
    return;
  }

  // 次の右側へのスクロールではみ出す場合は左側だけボタン表示
  if (scrollLeft + clientWidth >= scrollWidth) {
    isLeftBtnVisible.value = true;
    isRightBtnVisible.value = false;
    return;
  }

  // それ以外は両側ともボタン表示
  isLeftBtnVisible.value = true;
  isRightBtnVisible.value = true;
};

const scrollLeft = () => {
  if (scrollBox.value == null) return;

  const scrollTo = Math.max(0, scrollBox.value.scrollLeft - scrollBox.value.clientWidth);
  scroll(scrollTo);
  setBtnVisibility();
};

const scrollRight = () => {
  if (scrollBox.value == null) return;

  const scrollTo = Math.min(scrollBox.value.scrollWidth - scrollBox.value.clientWidth, scrollBox.value.scrollLeft + scrollBox.value.clientWidth);
  scroll(scrollTo);
  setBtnVisibility();
};

onMounted(() => {
  setBtnVisibility();
  scrollBox.value?.addEventListener('scroll', () => {
    setBtnVisibility();
  });

  // スロットの内容変更を監視
  if (scrollBox.value) {
    mutationObserver = new MutationObserver(() => {
      // DOM変更後、次のフレームでボタンの表示状態を更新
      nextTick(() => {
        setBtnVisibility();
      });
    });

    mutationObserver.observe(scrollBox.value, {
      childList: true,
      subtree: true,
    });
  }
});

onBeforeUnmount(() => {
  mutationObserver?.disconnect();
});

watch(
  () => props.refresh,
  (refresh) => {
    if (refresh) setBtnVisibility();
  },
);
</script>
